import { useChat } from '@ai-sdk/react';
import { Fragment, useEffect, useRef, useState } from 'react';
import { Streamdown } from 'streamdown';
import { DefaultChatTransport, lastAssistantMessageIsCompleteWithToolCalls } from 'ai'


const describeToolAction = (toolName: string, tense: 'present' | 'past'): string => {
    const map: Record<string, { present: string; past: string }> = {
        searchProducts: {
            present: 'Searching for relevant products',
            past: 'Searched for relevant products',
        },
        searchUsefulLinks: {
            present: 'Getting useful links',
            past: 'Got useful links',
        },
        clarifyQuery: {
            present: 'Thinking',
            past: 'Finished thinking',
        },
        searchKnowledge: {
            present: 'Searching my knowledge',
            past: 'Searched my knowledge',
        },
        searchSpecificProduct: {
            present: 'Searching for specific product',
            past: 'Searched for specific product',
        },
        searchEvents: {
            present: 'Searching for promotional events',
            past: 'Searched for promotional events',
        },
        searchServices: {
            present: 'Searching for services',
            past: 'Searched for services',
        },
    };

    const entry = map[toolName];
    if (entry) return entry[tense];

    // Fallbacks for unknown tools
    return tense === 'present' ? `Running ${toolName}` : `Completed ${toolName}`;
};

const renderProductCard = (product: any) => (
    <div key={product.variantId ?? product.id ?? product.sku} className="bg-white border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex gap-4">
            {product.imageUrl && (
                <img
                    src={product.imageUrl}
                    alt={product.imageAlt || product.title}
                    className="w-20 h-20 object-cover rounded-md flex-shrink-0"
                />
            )}
            <div className="flex-1">
                <h3 className="font-medium text-gray-900 mb-1">{product.title}</h3>
                {product.shortText && (
                    <p className="text-sm text-gray-600 mb-2">{product.shortText}</p>
                )}
                <div className="flex flex-wrap gap-2 text-xs text-gray-500 mb-2">
                    {product.brand && <span className="bg-gray-100 px-2 py-1 rounded">Brand: {product.brand}</span>}
                    {product.category && <span className="bg-gray-100 px-2 py-1 rounded">Category: {product.category}</span>}
                    {product.color && <span className="bg-gray-100 px-2 py-1 rounded">Color: {product.color}</span>}
                    {product.size && <span className="bg-gray-100 px-2 py-1 rounded">Size: {product.size}</span>}
                    {product.material && <span className="bg-gray-100 px-2 py-1 rounded">Material: {product.material}</span>}
                </div>
                <div className="flex items-center justify-between">
                    <span className="text-lg font-semibold text-gray-900">
                        {product.price} {product.currency}
                    </span>
                    <span
                        className={`text-sm px-2 py-1 rounded ${product.available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                    >
                        {product.available ? 'Available' : 'Out of Stock'}
                    </span>
                    <a href={product.link} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-500">View Product</a>
                </div>
            </div>
        </div>
    </div>
);

const renderSpecificProductCard = (product: any) => (
  <div key={product.variantId ?? product.id ?? product.sku} className="bg-white border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
    <div className="flex gap-4">
      {product.imageUrl && (
        <img
          src={product.imageUrl}
          alt={product.imageAlt || product.title}
          className="w-20 h-20 object-cover rounded-md flex-shrink-0"
        />
      )}
    </div>
    <div className="flex-1">
      <h3 className="font-medium text-gray-900 mb-1">{product.title}</h3>
      {product.shortText && (
        <p className="text-sm text-gray-600 mb-2">{product.shortText}</p>
      )}
      <div className="flex items-center justify-between">
        <span className="text-lg font-semibold text-gray-900">{product.price} {product.currency}</span>
        <span className={`text-sm px-2 py-1 rounded ${product.available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{product.available ? 'Available' : 'Out of Stock'}</span>
        <a href={product.link} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-500">View Product</a>
      </div>
    </div>
  </div>
);

export type ToolPart = {
    type: string
    state:
    | "input-streaming"
    | "input-available"
    | "output-available"
    | "output-error"
    input?: Record<string, unknown>
    output?: Record<string, unknown>
    toolCallId?: string
    errorText?: string
}

export type ToolProps = {
    toolPart: ToolPart
    className?: string
}

type ToolIconProps = {
    className?: string
}

const SettingsIcon = ({ className }: ToolIconProps) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24" className={className}><path fill="currentColor" d="m9.25 22l-.4-3.2q-.325-.125-.612-.3t-.563-.375L4.7 19.375l-2.75-4.75l2.575-1.95Q4.5 12.5 4.5 12.338v-.675q0-.163.025-.338L1.95 9.375l2.75-4.75l2.975 1.25q.275-.2.575-.375t.6-.3l.4-3.2h5.5l.4 3.2q.325.125.613.3t.562.375l2.975-1.25l2.75 4.75l-2.575 1.95q.025.175.025.338v.674q0 .163-.05.338l2.575 1.95l-2.75 4.75l-2.95-1.25q-.275.2-.575.375t-.6.3l-.4 3.2zm2.8-6.5q1.45 0 2.475-1.025T15.55 12t-1.025-2.475T12.05 8.5q-1.475 0-2.488 1.025T8.55 12t1.013 2.475T12.05 15.5"></path></svg>
)

const LoaderIcon = ({ className }: ToolIconProps) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24" className={className}><path fill="currentColor" d="M12 22c5.421 0 10-4.579 10-10h-2c0 4.337-3.663 8-8 8s-8-3.663-8-8c0-4.337 3.663-8 8-8V2C6.579 2 2 6.579 2 12c0 5.421 4.579 10 10 10z"></path></svg>
)

const CheckCircleIcon = ({ className }: ToolIconProps) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24" className={className}><path fill="currentColor" d="M12 22c5.421 0 10-4.579 10-10h-2c0 4.337-3.663 8-8 8s-8-3.663-8-8c0-4.337 3.663-8 8-8V2C6.579 2 2 6.579 2 12c0 5.421 4.579 10 10 10z"></path></svg>
)

const ErrorIcon = ({ className }: ToolIconProps) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24" className={className}><path fill="currentColor" d="M12 22c5.421 0 10-4.579 10-10h-2c0 4.337-3.663 8-8 8s-8-3.663-8-8c0-4.337 3.663-8 8-8V2C6.579 2 2 6.579 2 12c0 5.421 4.579 10 10 10z"></path></svg>
)

const Tool = ({ toolPart}: ToolProps) => {

    const { state } = toolPart

    const getStateIcon = () => {
        switch (state) {
            case 'input-streaming':
                return <LoaderIcon className="animate-spin text-blue-500 h-4 w-4" />
            case 'input-available':
                return <SettingsIcon className="text-orange-500 h-4 w-4" />
            case 'output-available':
                return <CheckCircleIcon className="text-green-500 h-4 w-4" />
            case 'output-error':
                return <ErrorIcon className="text-red-500 h-4 w-4" />
            default:
                return <LoaderIcon className="animate-spin text-muted-foreground h-4 w-4" />
        }
    }

    const getAccentColor = () => {
        switch (state) {
            case 'input-streaming':
                return 'text-blue-500'
            case 'input-available':
                return 'text-orange-500'
            case 'output-available':
                return 'text-green-500'
            case 'output-error':
                return 'text-red-500'
            default:
                return 'text-muted-foreground'
        }
    }

    return (
        <div className="border-border mt-3 overflow-hidden rounded-lg border">
            <div className="bg-background h-auto w-full justify-between rounded px-3 py-2 font-normal">
                <div className="flex items-center gap-2">
                    {getStateIcon()}
                    <span className="text-sm font-medium">{toolPart.type}</span>
                    {state !== "output-available" && state !== "output-error" && state !== "input-streaming" && (
                        <LoaderIcon className={"h-4 w-4 animate-spin " + getAccentColor()} />
                    )}

                </div>
            </div>

        </div>
    )
}

const renderToolWithDescription = (
    toolName: string,
    part: any,
    index: number,
    organizationType: 'ecommerce' | 'nonprofit'
) => {
    const tense = part.state === 'output-available' ? 'past' : 'present';
    const description = describeToolAction(toolName, tense as 'present' | 'past');
    const isNonprofit = organizationType === 'nonprofit';
    // Special rendering for searchProducts tool
    if (!isNonprofit && toolName === 'searchProducts' && part.state === 'output-available' && Array.isArray(part.output)) {
      return (
        <Fragment key={index}>
          <Tool
            toolPart={{
              type: description,
              state: part.state,
              input: part.input,
              // Hide raw JSON output on Tool and show custom cards below
              output: undefined,
              toolCallId: part.toolCallId,
              errorText: part.errorText,
            }}
          />
          <div className="mt-2 space-y-2">
            {part.output.map((product: any) => renderProductCard(product))}
          </div>
        </Fragment>
      );
    } else if (!isNonprofit && toolName === 'searchSpecificProduct' && part.state === 'output-available') {
      const product = Array.isArray(part.output)
      ? undefined
      : (part.output?.product ?? part.output);
      return (
        <Fragment key={index}>
          <Tool
            toolPart={{
              type: description,
              state: part.state,
              input: part.input,
              output: undefined,
              toolCallId: part.toolCallId,
              errorText: part.errorText,
            }}
          />
          <div className="mt-2 space-y-2">
            {product ? renderSpecificProductCard(product) : null}
          </div>
        </Fragment>
      );
    }
  
    return (
      <Tool
        key={index}
        toolPart={{
          type: description,
          state: part.state,
          input: part.input,
          output: part.output,
          toolCallId: part.toolCallId,
          errorText: part.errorText,
        }}
      />
    );
  };





export default function ChatWidget() {
    const params = new URLSearchParams(window.location.search);
    const chatbotId = params.get('chatbotId');
    const organizationType = params.get('organizationType') === 'nonprofit' ? 'nonprofit' : 'ecommerce';
    const logoUrl = params.get('logoUrl');
    const primaryColor = params.get('primaryColor');
    const defaultMessage = params.get('defaultMessage');
    const storeName = params.get('storeName');


    const { messages, sendMessage, status } = useChat({
        transport: new DefaultChatTransport({
            api: `https://backend.tovobot.com/api/chat/${chatbotId}`,
            credentials: 'include',
        }),
        messages: [
            {
                id: '1',
                role: 'assistant',
                parts: [
                    {
                        type: 'text',
                        text: defaultMessage || 'Hello, how can I help you today?',
                    }
                ]
            }
        ],
        sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,
    })

    const [input, setInput] = useState('');



    const messagesEndRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }

    if (!chatbotId) {
        return (
            <div className="flex items-center justify-center h-full p-4 text-red-500">
                Chat widget configuration is missing
            </div>
        );
    }

    useEffect(() => {
        scrollToBottom();
    }, [messages]);


    return (
        <div className="h-full w-full flex flex-col bg-white">
            {/* Header */}
            <div
                className={
                    "px-4 py-3 flex items-center gap-3 shadow-sm text-white"
                }
                style={{ background: primaryColor || '#2563eb' }}
            >
                <div className="flex items-center gap-2">
                    {logoUrl ? (
                        <img src={logoUrl} alt="logo" className="h-6 w-6 rounded-full object-cover bg-white" />
                    ) : (
                        <div className="h-6 w-6 rounded-full bg-white/20" />
                    )}
                    <span className="text-sm font-medium truncate">{storeName || 'Chat'}</span>
                </div>
            </div>

            {/* Messages */}
            <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
                {messages.map((message) => (
                    <div key={message.id} className="space-y-2">
                        {message.parts.map((part: any, i: number) => {
                            // tool rendering
                            if (typeof part.type === 'string' && part.type.startsWith('tool-')) {
                                const toolName = part.type.replace('tool-', '');
                                return renderToolWithDescription(toolName, part, i, organizationType);
                            }

                            if (part.type === 'text') {
                                return (
                                    <div key={`${message.id}-${i}`} className={message.role === 'assistant' ? 'flex items-start gap-2' : 'flex justify-end'}>
                                        {message.role === 'assistant' && (
                                            <div className="h-7 w-7 rounded-full bg-neutral-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                                                {logoUrl ? <img src={logoUrl} alt="logo" className="h-full w-full object-cover" /> : null}
                                            </div>
                                        )}
                                        <div className={message.role === 'assistant'
                                            ? 'max-w-[75%] rounded-2xl px-4 py-2 text-white bg-black'
                                            : 'max-w-[75%] rounded-2xl px-4 py-2 text-black bg-neutral-200'}
                                        >
                                            <Streamdown>{part.text}</Streamdown>
                                        </div>
                                    </div>
                                );
                            }
                            return null;
                        })}
                    </div>
                ))}

                <div ref={messagesEndRef} />
            </div>

            {/* Composer */}
            <form
                className="p-2 border-t border-neutral-200"
                onSubmit={e => {
                    e.preventDefault();
                    if (input.trim()) {
                        sendMessage({ text: input });
                        setInput('');
                    }
                }}
            >
                <div className="flex items-center gap-2">
                    <input
                        className={
                            "flex-1 rounded-full border border-neutral-300 px-4 py-2 text-sm outline-none"
                        }
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        disabled={status !== 'ready'}
                        placeholder="Reply"
                    />
                    <button
                        type="submit"
                        disabled={status !== 'ready' || !input.trim()}
                        className={
                            "rounded-full px-4 py-2 text-white text-sm disabled:opacity-50"
                        }
                        style={{ background: primaryColor || '#2563eb' }}
                    >
                        Send
                    </button>
                </div>
                <div className="mt-1 text-[10px] text-center text-neutral-400">Powered by <a href="https://www.tovobot.com" target="_blank" rel="noopener noreferrer" className="text-neutral-400">tovobot</a></div>
            </form>
        </div>
    )
}